import fetch from "./optionsFetch";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Header } from "./Header";
import { GetSession, GetToken, GetUserId } from "./Session";
import { NAVIGATION_ITEMS } from "./navigationItems";
import configData from "../config.json";
import logo from "../assets/ezyweigh-logo-transparent.png";
import "./Header.scss";

const normalizePath = (path) => path.toLowerCase().replace(/\/$/, "") || "/";
const DEFAULT_COMPANY_SITE_ID = 1;
const COMPANY_LOGO_UPDATED_EVENT = "wm-company-logo-updated";

const getUsableLogo = (value) => {
    if (!value || typeof value !== "string") return "";
    const logoValue = value.trim();
    if (!logoValue) return "";
    if (logoValue.startsWith("data:image/") || logoValue.startsWith("http://") || logoValue.startsWith("https://")) return logoValue;
    return "";
};

export const DrawerContainer = ({ children }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [drawerExpanded, setDrawerExpanded] = useState(() => window.innerWidth > 768);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [items, setItems] = useState([]);
    const [menuError, setMenuError] = useState("");
    const [pendingRoute, setPendingRoute] = useState(null);
    const [companyLogo, setCompanyLogo] = useState(logo);
    const currentPath = normalizePath(pathname);

    useEffect(() => {
        const controller = new AbortController();
        async function loadNavigation() {
            try {
                const response = await fetch(configData.SERVER_URL + "apiv7/options?userId=" + GetUserId(), {
                    headers: { Authorization: "Bearer " + GetToken() },
                    signal: controller.signal,
                });
                if (!response.ok) throw new Error("Unable to load navigation.");
                const options = await response.json();
                const isLocal = options.isLocal && GetSession().SiteID !== 1;
                const permissions = new Set(options.permissions);
                setItems(NAVIGATION_ITEMS.filter((item) => {
                    if (isLocal && item.route === "/sites") return false;
                    if (!isLocal && item.route === "/Weighbridge") return false;
                    return permissions.has(item.permission);
                }));
            } catch (error) {
                if (error.name !== "AbortError") setMenuError("Navigation could not be loaded. Please refresh the page.");
            }
        }
        loadNavigation();
        return () => controller.abort();
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadCompanyLogo() {
            try {
                const response = await fetch(configData.SERVER_URL + "apiv7/sites/" + DEFAULT_COMPANY_SITE_ID, {
                    headers: { Authorization: "Bearer " + GetToken() },
                    signal: controller.signal,
                });
                if (!response.ok) throw new Error("Unable to load company logo.");
                const site = await response.json();
                setCompanyLogo(getUsableLogo(site && site.fax) || logo);
            } catch (error) {
                if (error.name !== "AbortError") setCompanyLogo(logo);
            }
        }

        const updateCompanyLogo = (event) => {
            setCompanyLogo(getUsableLogo(event.detail && event.detail.logo) || logo);
        };

        loadCompanyLogo();
        window.addEventListener(COMPANY_LOGO_UPDATED_EVENT, updateCompanyLogo);
        return () => {
            controller.abort();
            window.removeEventListener(COMPANY_LOGO_UPDATED_EVENT, updateCompanyLogo);
        };
    }, []);

    const isActive = (item) => item.route && normalizePath(item.route) === currentPath;

    const goToPage = (route) => {
        if (currentPath !== "/weighbridge") localStorage.setItem("WBInUse", "false");
        navigate(route);
        if (window.innerWidth <= 768) setDrawerExpanded(false);
    };

    // Header and sidebar share the protection for an unfinished weighing transaction.
    const requestNavigation = (route) => {
        if (normalizePath(route) === currentPath) return;
        if (window.__wmExportInProgress) return;
        if (currentPath === "/weighbridge" && localStorage.getItem("WBInUse") === "true") {
            setPendingRoute(route);
            return;
        }
        goToPage(route);
    };

    const renderLink = (item, nested = false) => (
        <a key={item.id} href={item.route}
            className={"wm-sidebar__link" + (nested ? " wm-sidebar__link--nested" : "") + (isActive(item) ? " is-active" : "")}
            aria-current={isActive(item) ? "page" : undefined}
            onClick={(event) => {
                if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                requestNavigation(item.route);
            }}>
            <i className={item.icon} aria-hidden="true" />
            <span>{item.text}</span>
        </a>
    );

    const navigation = items.filter((item) => !item.parentid).map((item) => {
        if (item.route) return renderLink(item);
        const childItems = items.filter((child) => child.parentid === item.id);
        if (!childItems.length) return null;
        const hasActiveChild = childItems.some(isActive);
        const expanded = expandedGroups[item.id] ?? hasActiveChild;
        return (
            <div key={item.id}>
                <button type="button" className={"wm-sidebar__link" + (hasActiveChild ? " is-parent-active" : "")}
                    aria-expanded={expanded} aria-controls={"navigation-group-" + item.id}
                    onClick={() => setExpandedGroups((groups) => ({ ...groups, [item.id]: !expanded }))}>
                    <i className={item.icon} aria-hidden="true" />
                    <span>{item.text}</span>
                    <i className={"fa-solid fa-chevron-" + (expanded ? "down" : "right") + " wm-sidebar__chevron"} aria-hidden="true" />
                </button>
                <div id={"navigation-group-" + item.id} hidden={!expanded}>
                    {childItems.map((child) => renderLink(child, true))}
                </div>
            </div>
        );
    });

    return (
        <div className={"wm-shell" + (drawerExpanded ? " wm-shell--expanded" : "")}>
            {drawerExpanded && <button className="wm-shell__backdrop" aria-label="Close navigation" onClick={() => setDrawerExpanded(false)} />}
            <aside id="app-navigation" className="wm-sidebar" hidden={!drawerExpanded}>
                <a className="wm-sidebar__brand" href="/" aria-label="EZYweigh dashboard"
                    onClick={(event) => { event.preventDefault(); requestNavigation("/"); }}>
                    <img src={companyLogo} alt="Company logo" />
                </a>
                <nav aria-label="Main navigation" className="wm-sidebar__navigation">
                    {menuError && <p role="alert" className="wm-sidebar__message">{menuError}</p>}
                    {navigation}
                </nav>
                <footer className="wm-sidebar__footer">
                    <div>EZYweigh {configData.VERSION}</div>
                    <div>� {new Date().getFullYear()} Weigh-More Solutions</div>
                </footer>
            </aside>
            <div className="wm-shell__workspace">
                <Header drawerExpanded={drawerExpanded} onButtonClick={() => setDrawerExpanded((expanded) => !expanded)}
                    onNavigate={requestNavigation} />
                <div className="wm-shell__content">{children}</div>
            </div>
            {pendingRoute && (
                <Dialog title="Leave weighbridge?" onClose={() => setPendingRoute(null)}>
                    <p>A weighing transaction is in progress. Are you sure you want to continue?</p>
                    <DialogActionsBar>
                        <Button onClick={() => setPendingRoute(null)}>Stay here</Button>
                        <Button themeColor="primary" onClick={() => { goToPage(pendingRoute); setPendingRoute(null); }}>Continue</Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
};

export default DrawerContainer;
